import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { CanvasDataProvider } from './contexts/CanvasDataContext';
import AppRoutes from './routes';
import SessionMonitor from './components/common/SessionMonitor';
import PerformanceMonitor from './components/common/PerformanceMonitor';
import ApiTestPanel from './components/test/ApiTestPanel';
import InstallPrompt from './components/common/InstallPrompt';
import ErrorBoundary from './components/common/ErrorBoundary';
import { usePerformanceMonitor } from './hooks/usePerformanceMonitor';
import { startLastSyncedListener } from './services/lastSyncedStore';
import './App.css';

// Single QueryClient instance for the app's lifetime. Task 4.4a introduces
// this only for the courses read path (see CanvasDataContext); other read
// paths still go through the legacy canvasService/apiServiceManager stack.
const queryClient = new QueryClient();

// Import build info (will be generated during build)
let buildInfo: any = null;
let showVersion: any = null;
try {
  // Dynamic import for build info (generated during build process)
  import('./build-info.js').then(buildInfoModule => {
    buildInfo = buildInfoModule.getBuildInfo;
    showVersion = buildInfoModule.showVersion;

    // Update global functions if they've been set to fallbacks
    if (buildInfo && window.getBuildInfo) {
      window.getBuildInfo = buildInfo;
      const info = buildInfo();
      window.version = info.summary;
    }
    if (showVersion && window.showVersion) {
      window.showVersion = showVersion;
    }
  }).catch(() => {
    // Build info not available (development mode before first build)
    console.warn('Build info not available - run build script to generate');
  });
} catch (error) {
  // Build info not available (development mode before first build)
  console.warn('Build info not available - run build script to generate');
}

// Extend Window interface for debugging utilities
declare global {
  interface Window {
    canvasAppDebug?: {
      metrics: any;
      generateReport: () => any;
      trackNetworkRequest: (url: string, method?: string) => any;
    };
    showVersion?: () => any;
    getBuildInfo?: () => any;
    version?: string;
  }
}

/**
 * Performance wrapper component for monitoring
 */
const AppWithMonitoring = () => {
  const {
    metrics,
    trackRender,
    trackNetworkRequest,
    generateReport
  } = usePerformanceMonitor({
    enableRealTimeMetrics: import.meta.env.DEV,
    // `alert` is only logged, and usePerformanceMonitor is untyped JS, so
    // `unknown` is the honest annotation here: it claims nothing about the
    // payload's shape that this file could not check.
    onAlert: (alert: unknown) => {
      console.warn('Performance Alert:', alert);
    }
  });

  // Track app render performance
  useEffect(() => {
    const stopTracking = trackRender('App');
    return stopTracking;
  }, [trackRender]);

  // Expose performance utilities and version info globally for debugging
  useEffect(() => {
    if (import.meta.env.DEV) {
      window.canvasAppDebug = {
        metrics,
        generateReport,
        trackNetworkRequest
      };
    }

    // Always expose version functions (both dev and production)
    if (showVersion) {
      window.showVersion = showVersion;
    }
    if (buildInfo) {
      window.getBuildInfo = buildInfo;
      const info = buildInfo();
      window.version = info.summary;
    }

    // Provide fallback functions if build info not available
    if (!window.showVersion) {
      window.showVersion = () => {
        console.log('🚀 Canvas Companion - Build info not available');
        console.log('Run the build process to generate version information');
        return { version: 'development', buildId: 'dev', environment: 'development' };
      };
    }

    if (!window.getBuildInfo) {
      window.getBuildInfo = () => ({
        version: 'development',
        buildId: 'dev',
        environment: 'development',
        summary: 'Canvas Companion (development)'
      });
    }
  }, [metrics, generateReport, trackNetworkRequest]);

  return <AppRoutes />;
};

/**
 * Main App component that wraps the entire application with necessary providers
 * and global components like Header and Sidebar
 */
function App() {
  // Register service worker for PWA features.
  //
  // The path stays /sw.js (task 5.3a): the worker is now generated by
  // vite-plugin-pwa from src/sw.js, and keeping the same path and scope
  // means the broken worker already registered on real devices is replaced
  // rather than joined by a second one.
  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('Service Worker registered:', registration);

          // Check for updates
          registration.addEventListener('updatefound', () => {
            console.log('Service Worker update found');
          });
        })
        .catch(error => {
          console.error('Service Worker registration failed:', error);
        });
    }

    // Record how fresh the data the worker serves is. Read back by
    // LastSyncedNotice via the hooks/useLastSynced -> lastSyncedStore path
    // (task 5.3b); this remains the only place the listener is started.
    return startLastSyncedListener();
  }, []);

  // `beforeinstallprompt` used to be captured here only to console.log that
  // the app was installable, which threw the event away. Task 5.3b moved
  // capture into hooks/useInstallPrompt, mounted by <InstallPrompt /> below,
  // so the deferred event is actually kept and replayed at a chosen moment.

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <CanvasDataProvider>
              <SessionMonitor />
              <InstallPrompt />
              <PerformanceMonitor isVisible={import.meta.env.DEV} />
              <ApiTestPanel isVisible={import.meta.env.DEV} />
              <AppWithMonitoring />
            </CanvasDataProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;